/*
 * Leaderboard — TV / projector mode.
 *
 * Two big team panels coloured by brand, then per round a quiet small-caps
 * tag and full-width match bars coloured by the leading side. Use case:
 * project on a real television during the trip. The everyday leaderboard
 * is the editorial Leaderboard.tsx view.
 *
 * Route: /app/tournaments/:eventId/leaderboard/tv
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Triangle, Trophy } from 'lucide-react';

import { api } from '@/lib/api';
import { formatTeamCupPoints } from '@/lib/teamCupPoints';
import { AppShell } from '@/components/AppShell';
import { formatLabel, winningRuleLabel } from './_shared';
import type {
  EventLeaderboard,
  LeaderboardMatch,
  LeaderboardMatchParticipant,
  LeaderboardTeam,
} from '@golf-app/shared';

export default function Leaderboard() {
  const { eventId } = useParams<{ eventId: string }>();
  const [lb, setLb] = useState<EventLeaderboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!eventId) return;
    setError(null);
    try { setLb(await api.getEventLeaderboard(eventId)); }
    catch (e: any) { setError(e?.message ?? "Couldn't load leaderboard"); }
    finally { setLoading(false); }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }
  if (error || !lb) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
        <div className="t-empty"><div className="t-empty-title">{error ?? 'No leaderboard yet'}</div></div>
      </AppShell>
    );
  }

  const teamA = lb.teams[0];
  const teamB = lb.teams[1];
  const teamMap = new Map(lb.teams.map((t) => [t.id, t]));

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }}>
      <div className="t-bc-hero">
        <h1 className="t-bc-hero-title">{lb.event.name}</h1>
        {/* Subtitle slot — left to format/round below */}
      </div>

      {teamA && teamB && <TeamPanels a={teamA} b={teamB} />}

      <div className="t-bc-status">
        {lb.isClinched && lb.leaderTeamId ? (
          <span className="t-bc-status-clinch">
            <Trophy width={16} height={16} /> {teamMap.get(lb.leaderTeamId)?.name} clinched the cup
          </span>
        ) : (
          <span>First to {formatTeamCupPoints(lb.pointsNeeded)} · {formatTeamCupPoints(lb.pointsRemaining)} {lb.pointsRemaining === 1 ? 'point' : 'points'} remaining · {winningRuleLabel(lb.event.winningRuleType)}</span>
        )}
      </div>

      {/* Rounds */}
      {lb.rounds.length === 0 ? (
        <div className="t-empty"><div className="t-empty-title">No rounds yet</div></div>
      ) : (
        lb.rounds.map((r) => {
          const aPts = teamA ? (r.teamPoints[teamA.id] ?? 0) : 0;
          const bPts = teamB ? (r.teamPoints[teamB.id] ?? 0) : 0;
          return (
            <div key={r.id}>
              <div className="t-bc-round-tag">
                <div className="t-bc-round-tag-main">
                  <span className="t-bc-round-tag-line" />
                  <span className="t-bc-round-tag-text">{r.name}  ·  {formatLabel(r.formatType)}</span>
                  <span className="t-bc-round-tag-line" />
                </div>
                {teamA && teamB && (
                  <span className="t-bc-round-tag-points">
                    {teamA.name} {formatTeamCupPoints(aPts)} — {formatTeamCupPoints(bPts)} {teamB.name}
                  </span>
                )}
              </div>
              <div className="t-bc-matches">
                {r.matches.map((m) => (
                  <MatchBar key={m.id} m={m} teamA={teamA} teamB={teamB} />
                ))}
              </div>
            </div>
          );
        })
      )}
    </AppShell>
  );
}

function TeamPanels({ a, b }: { a: LeaderboardTeam; b: LeaderboardTeam }) {
  return (
    <div className="t-bc-panels">
      <div className="t-bc-panel t-bc-panel-left" style={{ background: a.primaryColor ?? '#7f1d1d' }}>
        <PanelName full={a.name} />
        <span className="t-bc-panel-points">{formatTeamCupPoints(a.totalPoints)}</span>
      </div>
      <div className="t-bc-panel t-bc-panel-right" style={{ background: b.primaryColor ?? '#1e3a8a' }}>
        <PanelName full={b.name} />
        <span className="t-bc-panel-points">{formatTeamCupPoints(b.totalPoints)}</span>
      </div>
    </div>
  );
}

function PanelName({ full }: { full: string }) {
  return (
    <span className="t-bc-panel-name">
      <span className="t-bc-panel-name-full">{full}</span>
      <span className="t-bc-panel-name-short">{full}</span>
    </span>
  );
}

function MatchBar({
  m, teamA, teamB,
}: {
  m: LeaderboardMatch;
  teamA: LeaderboardTeam | undefined;
  teamB: LeaderboardTeam | undefined;
}) {
  const aLeads = m.leadingTeamId && m.leadingTeamId === m.teamAId;
  const bLeads = m.leadingTeamId && m.leadingTeamId === m.teamBId;
  const isFinal = m.status === 'final';
  const isDraft = m.status === 'draft' || m.status === 'published' || m.status === 'cancelled';
  const isDisputed = m.status === 'disputed';

  const leaderColor = aLeads ? (teamA?.primaryColor ?? '#7f1d1d')
                    : bLeads ? (teamB?.primaryColor ?? '#1e3a8a')
                    : '#1a1d20';
  const leaderTeamColor = aLeads ? (teamA?.primaryColor) : bLeads ? (teamB?.primaryColor) : undefined;

  const sideA = m.participants.filter((p) => p.sideNumber === 1);
  const sideB = m.participants.filter((p) => p.sideNumber === 2);

  const sideACls = aLeads ? 't-bc-match-side-bg-leader' : bLeads ? 't-bc-match-side-bg-loser' : 't-bc-match-side-bg-neutral';
  const sideBCls = bLeads ? 't-bc-match-side-bg-leader' : aLeads ? 't-bc-match-side-bg-loser' : 't-bc-match-side-bg-neutral';

  // Build the center score block
  const centerNode = (() => {
    if (isDraft) return <span className="t-bc-margin-vs">VS</span>;
    if (isFinal) {
      // "3&2", "1 Up", "Halved"
      const label = m.finalResultDisplay ?? '—';
      return (
        <>
          <span className="t-bc-margin" style={{ color: m.finalResultType === 'tie' ? 'var(--text-sec)' : '#fff' }}>{label}</span>
          <span className="t-bc-thru t-bc-thru-final">FINAL</span>
        </>
      );
    }
    if (isDisputed) {
      return (
        <>
          <span className="t-bc-margin" style={{ color: 'var(--red)' }}>{m.finalResultDisplay ?? '—'}</span>
          <span className="t-bc-thru t-bc-thru-disputed">DISPUTED</span>
        </>
      );
    }
    // in_progress / pending_confirmation
    if (m.marginAbs === 0) {
      return (
        <>
          <span className="t-bc-margin t-bc-margin-as">AS</span>
          {m.holesPlayed > 0 && <span className="t-bc-thru">THRU {m.holesPlayed}</span>}
        </>
      );
    }
    return (
      <>
        <span className={`t-bc-margin ${aLeads ? 't-bc-margin-a' : 't-bc-margin-b'}`} style={leaderTeamColor ? ({ ['--leader-color' as any]: leaderTeamColor }) : undefined}>
          {m.marginAbs}
          <Triangle fill="currentColor" stroke="currentColor" />
        </span>
        {m.holesPlayed > 0 && <span className="t-bc-thru">THRU {m.holesPlayed}</span>}
      </>
    );
  })();

  return (
    <div
      className="t-bc-match"
      style={leaderColor ? ({ ['--leader-color' as any]: leaderColor }) : undefined}
    >
      <div className={`t-bc-match-side t-bc-match-side-a ${sideACls}`}>
        {sideA.length === 0
          ? <span className="t-bc-player-tbd">TBD</span>
          : sideA.map((p, i) => <PlayerName key={i} p={p} />)}
      </div>
      <div className="t-bc-match-center">{centerNode}</div>
      <div className={`t-bc-match-side t-bc-match-side-b ${sideBCls}`}>
        {sideB.length === 0
          ? <span className="t-bc-player-tbd">TBD</span>
          : sideB.map((p, i) => <PlayerName key={i} p={p} />)}
      </div>
    </div>
  );
}

function PlayerName({ p }: { p: LeaderboardMatchParticipant }) {
  return <span className="t-bc-player">{p.displayName}</span>;
}
